import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useTheme, alpha, darken, lighten } from '@mui/material/styles'

// Lightweight full-screen overlay driven by Redux flag `paymentInProcess`
function PaymentProgressOverlay() {
  const theme = useTheme()
  const isProcessing = useSelector((state: { paymentInProcess: boolean }) =>
    Boolean(state.paymentInProcess)
  )

  // Beautiful staged messages to convey progress
  const stages = useMemo(
    () => [
      'Verifying payment information',
      'Creating secure payment intent',
      'Confirming with your bank',
      'Placing your order',
      'Sending order to the kitchen',
      'Finalizing order details',
      'Almost done…',
    ],
    []
  )

  const [stageIndex, setStageIndex] = useState(0)
  const intervalRef = useRef<number | null>(null)

  // Progress width based on stage index (visual only)
  const progressPercent = useMemo(() => {
    if (!stages.length) return 0
    const ratio = stageIndex / (stages.length - 1)
    const clamped = Math.max(0, Math.min(1, ratio))
    return Math.round(clamped * 100)
  }, [stageIndex, stages.length])

  useEffect(() => {
    if (isProcessing) {
      setStageIndex(0)
      if (intervalRef.current) window.clearInterval(intervalRef.current)
      // Advance stage periodically to give a sense of progress
      intervalRef.current = window.setInterval(() => {
        setStageIndex((prev) => (prev < stages.length - 1 ? prev + 1 : prev))
      }, 2200)
    } else {
      // Cleanup and reset when processing ends
      if (intervalRef.current) window.clearInterval(intervalRef.current)
      intervalRef.current = null
      setTimeout(() => setStageIndex(0), 150)
    }

    return () => {
      if (intervalRef.current) window.clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [isProcessing, stages.length])

  if (!isProcessing) return null

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Processing your payment"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: (theme.zIndex?.modal || 1300) + 2,
        background: `linear-gradient(0deg, ${alpha(
          theme.palette.primary.main,
          0.06
        )}, ${alpha(theme.palette.primary.main, 0.06)}), ${
          theme.palette.background.default
        }`,
        color: theme.palette.text.primary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          width: 'min(600px, 92vw)',
          padding: '28px 24px',
          borderRadius: 16,
          backdropFilter: 'blur(6px)',
          background: alpha(theme.palette.background.paper, 0.95),
          boxShadow: '0 10px 30px rgba(0,0,0,0.20)',
        }}
      >
        {/* Spinner */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            marginBottom: 16,
          }}
        >
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              border: `3px solid ${alpha(theme.palette.text.primary, 0.15)}`,
              borderTopColor: theme.palette.primary.main,
              animation: 'spin 900ms linear infinite',
              flex: '0 0 auto',
            }}
          />
          <div>
            <div
              style={{
                fontSize: 18,
                fontWeight: 700,
                letterSpacing: 0.2,
              }}
            >
              Processing your order
            </div>
            <div
              style={{
                opacity: 0.75,
                marginTop: 2,
                fontSize: 14,
              }}
            >
              Please keep this tab open. This usually takes a few moments.
            </div>
          </div>
        </div>

        {/* Stage message */}
        <div
          style={{
            marginTop: 4,
            marginBottom: 12,
            fontSize: 16,
          }}
        >
          {stages[stageIndex]}
        </div>

        {/* Progress bar */}
        <div
          aria-hidden
          style={{
            height: 8,
            width: '100%',
            background: alpha(theme.palette.text.primary, 0.12),
            borderRadius: 999,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              height: '100%',
              width: `${progressPercent}%`,
              background: `linear-gradient(90deg, ${lighten(
                theme.palette.primary.main,
                0.15
              )} 0%, ${theme.palette.primary.main} 50%, ${darken(
                theme.palette.primary.main,
                0.15
              )} 100%)`,
              transition: 'width 600ms ease',
            }}
          />
        </div>

        {/* Subtext with animated dots */}
        <div
          style={{
            marginTop: 10,
            opacity: 0.7,
            fontSize: 13,
          }}
        >
          Do not refresh or navigate away while we complete your checkout…
        </div>
      </div>

      {/* Keyframes */}
      <style>{`
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
`}</style>
    </div>
  )
}

export default PaymentProgressOverlay
